import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CommonWindow } from '@interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from './auth';
import { UrlService } from './services/url.service';
import { ConfigService } from './commons/config/config.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public isLoading = true;
  public appReady = false;
  public error = false;
  public currentRoute: string = null;
  public previousUrl: string = null;
  private secondPreviousUrl: string;
  public avatarName = 'A';
  public isFullScreenRoute = false;
  public isMenuOpen = false;
  private chatLoaded = false;
  private isUserSpanish = true;
  public fullScreenList = [
    '/risk-warning',
    '/new-contract-pymes',
    '/new-contract-large-accounts',
    '/campaigns/campaign-detail',
    '/campaigns/new-campaign',
    '/customers/all/internal',
    '/customers/all/internal/bill',
    '/customers/all/pymes',
    '/customers/all/pymes/bill',
    '/customers/next-expiration/contract',
    '/tracing/contract-detail',
    '/customers/contract-detail',
    '/contracts/contract-detail',
    '/draft',
    '/incidents/new',
    '/incidents/incidence-detail',
    '/new-offer',
    '/incidents/detail',
    '/user-management',
    '/renovate-contract-pymes',
    '/renovate-contract-multipoint',
    '/new-contract-pymes-multipoint',
    '/unsubscribe-commercial',
    '/change-product',
    '/change-product-multipoint',
    '/customers/change-product',
    '/co2/new',
    '/co2/detail'
  ];
  public fullScreenRouteList = [
    '/new-contract-pymes',
    '/new-contract-large-accounts',
    '/campaigns/campaign-detail',
    '/campaigns/new-campaign',
    '/customers/all/internal',
    '/customers/all/internal/bill',
    '/customers/all/pymes',
    '/customers/all/pymes/bill',
    '/customers/next-expiration/contract',
    '/tracing/contract-detail',
    '/customers/contract-detail',
    '/contracts/contract-detail',
    '/draft',
    '/incidents/new',
    '/incidents/incidence-detail',
    '/new-offer',
    '/incidents/detail',
    '/user-management',
    '/renovate-contract-pymes',
    '/renovate-contract-multipoint',
    '/new-contract-pymes-multipoint',
    '/unsubscribe-commercial',
    '/change-product',
    '/change-product-multipoint',
    '/customers/change-product',
    '/co2/new',
    '/co2/detail'
  ];

  @ViewChild('main') mainChild: ElementRef;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private auth: AuthService,
    private urlService: UrlService,
    private config: ConfigService,
    @Inject('Window') protected window: CommonWindow,
    @Inject(DOCUMENT) protected document: Document
  ) {
    this.subscribeAuth();
  }

  ngOnInit() {
    this.currentRoute = location.pathname;
    this.subscribeRouter();
    this.router.navigate(['/']);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.mainChild.nativeElement.className === 'menuOpened') {
      this.mainChild.nativeElement.className = '';
    } else {
      this.mainChild.nativeElement.className = 'menuOpened';
    }
  }

  public retry(): void {
    this.closeChat();
    this.auth.logout();
  }

  private subscribeAuth(): void {
    this.auth.hasError.pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.error = res;
      this.closeChat();
    });
    this.auth.isLoading.pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.isLoading = res;
    });
    this.auth.ready.pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.appReady = res;
      if (this.appReady) {
        this.isUserSpanish = this.auth.getUserData().country === 'ES';
        const isMasterOrBackoffice = this.auth.getRole()?.code === '300000006' || this.auth.getRole()?.code === '300000005';
        if (this.isUserSpanish && isMasterOrBackoffice) {
          this.createMSOmnichannel(this.auth.getUserData()?.email);
        }
      }
    });
  }
  
  private closeChat(): void {
    if (this.chatLoaded) {
      this.window.zE('messenger', 'logoutUser');
      this.window.zE('messenger:set', 'cookies', false);
    }
  }

  private subscribeRouter(): void {
    this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        if (this.fullScreenList.includes(e.url.split('?')[0])) {
          this.renderer.addClass(this.mainChild.nativeElement, 'full-screen');
        } else {
          this.renderer.removeClass(this.mainChild.nativeElement, 'full-screen');
        }
        this.isFullScreenRoute = this.fullScreenRouteList.includes(e.url.split('?')[0]);
        this.secondPreviousUrl = this.previousUrl;
        this.previousUrl = this.currentRoute;
        this.currentRoute = e.url !== '/' ? e.url : location.pathname;
        this.urlService.setPreviousUrl(this.previousUrl, this.secondPreviousUrl);
      }
    });
  }

  private createMSOmnichannel(userEmail: string): void {
    const element = document.createElement('script');
    element.id = 'Microsoft_Omnichannel_LCWidget';
    element.src = 'https://oc-cdn-public-eur.azureedge.net/livechatwidget/scripts/LiveChatBootstrapper.js';
    element.onerror = () => {
      const el = element;
      el.parentNode.removeChild(el);
      var s=document.createElement('script');
      s.src='https://ocprodpubliceurgs.blob.core.windows.net/livechatwidget/scripts/LiveChatBootstrapper.js';
      s.setAttribute('id', 'Microsoft_Omnichannel_LCWidget');
      s.setAttribute('data-app-id', this.config.get('auth.omniChannelDataAppId'));
      s.setAttribute('data-lcw-version', 'prod');
      s.setAttribute('data-org-id', this.config.get('auth.omniChannelDataOrgId'));
      s.setAttribute('data-org-url', this.config.get('auth.omniChannelDataOrgUrl'));
      document.body.appendChild(s);
    };
    element.setAttribute('data-app-id', this.config.get('auth.omniChannelDataAppId'));
    element.setAttribute('data-lcw-version', 'prod');
    element.setAttribute('data-org-id', this.config.get('auth.omniChannelDataOrgId'));
    element.setAttribute('data-org-url', this.config.get('auth.omniChannelDataOrgUrl'));
    element.async;
    this.document.body.appendChild(element);
    this.createScriptToSendEmail(userEmail);
  }

  private createScriptToSendEmail(email: string): void {
    const element = document.createElement('script');
    element.type = 'text/javascript';
    element.text = 'function contextProvider() {\n'+
        ' return {\n'+
          ' "Email": "' + email + '"\n'+

        ' };\n'+
      '}\n'+

      'window.addEventListener("lcw:ready", function () {\n'+
      ' Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(contextProvider);\n'+
      // 'Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();\n'+
      '});';
    this.document.body.appendChild(element);
  }

}
